package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.screenshots.doc.ImageFit
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.NRect
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.geom.Rect

internal fun paintImage(layer: ImageLayer, rect: Rect, image: ImageHandle?, mode: RenderMode, palette: Palette, slot: Int, surface: Surface) {
    if (image == null && layer.source is ImageSource.Capture && mode == RenderMode.EXPORT) return
    val path = Paths.roundedRect(rect, layer.cornerRadius)
    if (image != null) {
        surface.save()
        try {
            surface.clip(path)
            drawFittedImage(surface, image, rect, layer.fit, layer.crop)
        } finally { surface.restore() }
    } else if (layer.source is ImageSource.Capture && mode == RenderMode.EDITOR) {
        paintMissingCapture(surface, path, rect)
    }
    layer.stroke?.let { surface.stroke(path, resolvePaint(it.paint, rect, palette, slot), StrokeStyle(it.width, dash = it.dash)) }
}

/** Resolves crop and aspect before crossing the platform's drawImage boundary. */
internal fun drawFittedImage(surface: Surface, image: ImageHandle, dst: Rect, fit: ImageFit, crop: NRect? = null) {
    require(image.width > 0 && image.height > 0) { "Image dimensions must be positive" }
    val source = crop?.let { Rect(it.x * image.width, it.y * image.height, it.w * image.width, it.h * image.height) }
        ?: Rect(0.0, 0.0, image.width.toDouble(), image.height.toDouble())
    if (source.w <= 0 || source.h <= 0 || dst.w <= 0 || dst.h <= 0) return
    if (fit == ImageFit.CONTAIN) {
        val scale = minOf(dst.w / source.w, dst.h / source.h)
        val w = source.w * scale
        val h = source.h * scale
        surface.drawImage(image, source, Rect(dst.x + (dst.w - w) / 2, dst.y + (dst.h - h) / 2, w, h))
    } else {
        val scale = maxOf(dst.w / source.w, dst.h / source.h)
        val w = dst.w / scale
        val h = dst.h / scale
        surface.drawImage(image, Rect(source.x + (source.w - w) / 2, source.y + (source.h - h) / 2, w, h), dst)
    }
}

internal fun paintMissingCapture(surface: Surface, screenPath: Path, rect: Rect) {
    surface.save()
    try {
        surface.setShadow(null)
        surface.clip(screenPath)
        surface.fill(Paths.rect(rect), ResolvedPaint.Solid(0xFFE0E0E0))
        var offset = -rect.h
        while (offset < rect.w) {
            surface.stroke(PathBuilder().moveTo(rect.x + offset, rect.bottom).lineTo(rect.x + offset + rect.h, rect.y).build(),
                ResolvedPaint.Solid(0xFFB8B8B8), StrokeStyle(2.0))
            offset += 12.0
        }
    } finally { surface.restore() }
}
